import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PitchQuickModal } from './PitchQuickModal';
import type { TestPitchMetric } from '@/types/pitch';
import { pitchLabel, toVideoPitches } from '@/utils/pitches';

interface PitchRetentionCellProps {
  /** Pitch que preenche a célula (escolhido pelo seletor). null = vídeo sem pitch nessa posição. */
  pitch: TestPitchMetric | null;
  /** Todos os pitches do vídeo dono: o PUT substitui a lista inteira. */
  videoPitches: TestPitchMetric[];
  /**
   * Vídeo DONO dos pitches — é nele que o PUT grava. Nos testes que rodam sobre
   * clones (speed, headline, autoplay) os pitches moram no vídeo ORIGINAL, e o
   * backend devolve esse id em `metric.pitchVideoId`.
   */
  pitchVideoId?: string | null;
  /** Duração do vídeo em segundos — o modal valida que o pitch não a excede. */
  durationInSeconds?: number | null;
  /** Mostra o nome do pitch embaixo da % (quando o seletor tem 2+ opções). */
  showName?: boolean;
  /** Chamado após salvar, para o pai refazer o fetch das métricas. */
  /** Pode devolver a promessa do recarregamento: o modal só fecha depois dela. */
  onSaved?: () => unknown;
  className?: string;
}

/**
 * Célula de "Retenção no Pitch" das tabelas de teste.
 *
 * Com pitch: mostra a % (e o nome, se pedido); clicar abre o PitchQuickModal
 * editando esse pitch. Sem pitch: "Definir" abre o modal criando um pitch novo
 * no vídeo dono. Sem `pitchVideoId` não há onde gravar — fica só leitura.
 */
export function PitchRetentionCell({
  pitch,
  videoPitches,
  pitchVideoId,
  durationInSeconds,
  showName = false,
  onSaved,
  className,
}: PitchRetentionCellProps) {
  useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const canEdit = !!pitchVideoId;

  const pitchIndex = pitch ? videoPitches.findIndex((p) => p.id === pitch.id) : -1;
  const name = pitch ? pitchLabel(pitch, Math.max(pitchIndex, 0)) : null;
  const value = pitch?.retentionPct != null ? `${pitch.retentionPct.toFixed(1)}%` : '-';

  return (
    <>
      <div className={cn('flex flex-col items-end gap-0.5 group', className)}>
        {pitch ? (
          canEdit ? (
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              title={translateInterfaceText("Clique para ajustar o pitch")}
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[13px] font-medium transition-colors hover:text-primary hover:underline underline-offset-2 cursor-pointer"
            >
              {value}
              <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ) : (
            <span className="text-[13px] font-medium">{value}</span>
          )
        ) : canEdit ? (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            title={translateInterfaceText("Pitch não configurado — clique para definir")}
            className="rounded px-1 py-0.5 text-[13px] font-medium text-primary hover:underline underline-offset-2"
          >
            {translateInterfaceText("Definir")}</button>
        ) : (
          <span className="text-[13px] font-medium text-muted-foreground">-</span>
        )}
        {showName && name && (
          <span className="max-w-[140px] truncate text-[11px] text-muted-foreground" title={name}>
            {name}
          </span>
        )}
      </div>

      {canEdit && (
        <PitchQuickModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          videoId={pitchVideoId ?? null}
          pitches={toVideoPitches(videoPitches)}
          editingPitchId={pitch?.id ?? null}
          durationInSeconds={durationInSeconds}
          onSaved={async () => {
            await onSaved?.();
          }}
        />
      )}
    </>
  );
}
